import React, { useState, useEffect } from "react";
import { makeRequestAuth } from "../../../_config/api";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  ShieldCheck,
  CreditCard,
  Calendar,
  MapPin,
  Clock,
  AlertCircle,
  CheckCircle2,
  Edit,
  Globe,
  FileText
} from "lucide-react";

const emptyData = {
  passportNumber: "",
  dateOfIssue: "",
  dateOfExpiry: "",
  placeOfIssue: "",
};

const ReferenceOtherForm = () => {
  const [formData, setFormData] = useState(emptyData);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  useEffect(() => {
    fetchAPi();
  }, []);

  const fetchAPi = async () => {
    setLoading(true);
    try {
      const response = await makeRequestAuth("get", "/user/getPassport");
      if (response?.data?.passportNumber) {
        const apiData = response.data;
        const formattedData = {
          ...apiData,
          dateOfIssue: apiData.dateOfIssue ? apiData.dateOfIssue.split('T')[0] : "",
          dateOfExpiry: apiData.dateOfExpiry ? apiData.dateOfExpiry.split('T')[0] : "",
        };
        setPreview(formattedData);
        setFormData(formattedData);
      }
    } catch (err) {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    let errs = [];
    const today = new Date().toISOString().split('T')[0];
    if (!formData.passportNumber.trim()) errs.push("Passport Number is required");
    if (!formData.dateOfIssue) errs.push("Date of Issue is required");
    if (formData.dateOfIssue > today) errs.push("Issue Date cannot be in the future");
    if (!formData.dateOfExpiry) errs.push("Date of Expiry is required");
    if (formData.dateOfExpiry < today) errs.push("Passport has expired. Please check Expiry Date");
    if (formData.dateOfIssue && formData.dateOfExpiry) {
      if (new Date(formData.dateOfExpiry) <= new Date(formData.dateOfIssue)) {
        errs.push("Expiry Date must be after Issue Date");
      }
    }
    if (!formData.placeOfIssue.trim()) errs.push("Place of Issue is required");
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setErrors([]);
    setLoading(true);
    try {
      await makeRequestAuth("post", "/user/createOrUpdatePassport", formData);
      showSuccessToast("Passport records updated successfully ✅");
      fetchAPi();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const STATUS_CONFIG = {
    pending: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: <Clock size={14} />, label: "Pending" },
    approve: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: <CheckCircle2 size={14} />, label: "Approve" },
    reject: { color: "bg-rose-100 text-rose-700 border-rose-200", icon: <AlertCircle size={14} />, label: "Rejected" },
  };

  return (
    <div className="max-w-4xl mx-auto py-10 px-4 font-normal">
      <ToastContainer />

      {/* HEADER SECTION */}
      <div className="bg-[#132b45] rounded-t-[2rem] p-8 text-white relative overflow-hidden shadow-2xl">
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500/20 rounded-2xl">
              <ShieldCheck size={30} className="text-blue-300" />
            </div>
            <div>
              <h1 className="text-2xl font-medium">Passport Identification</h1>
              <p className="text-blue-100/60 text-xs mt-1 font-normal">Official travel document details</p>
            </div>
          </div>
          {preview && (
            <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full border text-[10px] font-normal uppercase tracking-widest shadow-sm ${STATUS_CONFIG[preview.status?.toLowerCase()]?.color || "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {STATUS_CONFIG[preview.status?.toLowerCase()]?.icon || <Clock size={14} />}
              {STATUS_CONFIG[preview.status?.toLowerCase()]?.label || "Processing"}
            </div>
          )}
        </div>
        <Globe className="absolute -right-10 -bottom-10 text-white/5 w-48 h-48" />
      </div>

      <div className="bg-white rounded-b-[2rem] shadow-xl border-x border-b border-gray-100 p-8">
        {/* ERROR DISPLAY */}
        {errors.length > 0 && (
          <div className="mb-8 bg-rose-50 border-l-4 border-rose-500 p-5 rounded-r-2xl animate-in fade-in duration-300">
            <div className="flex items-center gap-2 text-rose-800 font-medium mb-2">
              <AlertCircle size={18} /> Please correct the following:
            </div>
            <ul className="text-sm text-rose-600 space-y-1 ml-6 list-disc font-normal">
              {errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {preview && !errors.length > 0 && preview.passportNumber && !loading ? (
          <div className="space-y-10 animate-in fade-in zoom-in-95 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <PreviewItem icon={<CreditCard className="text-blue-500" />} label="Passport Number" value={preview.passportNumber} />
              <PreviewItem icon={<MapPin className="text-emerald-500" />} label="Place of Issue" value={preview.placeOfIssue} />
              <PreviewItem icon={<Calendar className="text-indigo-500" />} label="Date of Issue" value={formatDate(preview.dateOfIssue)} />
              <PreviewItem icon={<Calendar className="text-rose-500" />} label="Date of Expiry" value={formatDate(preview.dateOfExpiry)} />
            </div>

            {(preview.status === "reject" || !preview.status) && (
              <div className="pt-8 border-t border-gray-100 flex justify-center">
                <button
                  onClick={() => {
                    setPreview(null);
                    setFormData(emptyData);
                  }}
                  className="flex items-center gap-2 bg-[#132b45] hover:bg-black text-white px-10 py-4 rounded-2xl font-normal transition-all shadow-xl active:scale-95"
                >
                  <Edit size={18} /> Modify Passport Info
                </button>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Passport Number"
                icon={<CreditCard size={18} />}
                placeholder="A1234567"
                value={formData.passportNumber}
                onChange={(v) => handleChange("passportNumber", v)}
                required
              />
              <Input
                label="City/Country of Issue"
                icon={<MapPin size={18} />}
                placeholder="Ex: Mumbai, India"
                value={formData.placeOfIssue}
                onChange={(v) => handleChange("placeOfIssue", v)}
                required
              />
              <Input
                type="date"
                label="Date of Issue"
                icon={<Calendar size={18} />}
                value={formData.dateOfIssue}
                max={new Date().toISOString().split('T')[0]}
                onChange={(v) => handleChange("dateOfIssue", v)}
                required
              />
              <Input
                type="date"
                label="Date of Expiry"
                icon={<Calendar size={18} />}
                value={formData.dateOfExpiry}
                min={new Date().toISOString().split('T')[0]}
                onChange={(v) => handleChange("dateOfExpiry", v)}
                required
              />
            </div>

            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#132b45] hover:bg-black text-white py-5 rounded-2xl font-normal text-lg shadow-2xl transition-all active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-70"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <FileText size={20} /> Finalize Passport Details
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/* --- UPDATED REUSABLE INPUT (NORMAL FONT) --- */
const Input = ({ label, value, onChange, type = "text", icon, placeholder, required, max, min }) => (
  <div className="space-y-2">
    <label className="text-sm font-normal text-slate-700 ml-1 flex items-center gap-1">
      {label} {required && <span className="text-rose-500">*</span>}
    </label>
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#132b45] transition-colors">
        {icon}
      </div>
      <input
        type={type}
        value={value}
        max={max}
        min={min}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl pl-12 pr-5 py-3.5 focus:bg-white focus:border-[#132b45] outline-none transition-all font-normal text-slate-800 placeholder:text-slate-300"
      />
    </div>
  </div>
);

/* --- UPDATED PREVIEW ITEM (NORMAL FONT) --- */
const PreviewItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-4 p-5 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-lg transition-all duration-300">
    <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-50 text-slate-600">
      {icon}
    </div>
    <div>
      <p className="text-[10px] uppercase font-normal text-slate-400 tracking-widest leading-none mb-1.5">{label}</p>
      <p className="text-[#132b45] font-normal text-base leading-tight uppercase">{value || "-"}</p>
    </div>
  </div>
);

export default ReferenceOtherForm;